import type { SingleValue } from "react-select";

import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { SelectInput } from "components/Select";
import { Input } from "components/ui/input";

import {
  DOCUMENT_TYPE_OPTIONS,
  FilterOption,
  INVOICE_STATUS_OPTIONS,
  SALE_STATUS_OPTIONS,
  STORE_INVOICE_LABELS,
} from "../types";

interface StoreInvoiceFiltersProps {
  readonly storeOptions: readonly FilterOption[];
  readonly storeCode: number | undefined;
  readonly isStoreLocked: boolean;
  readonly onStoreChange: (storeCode: number | undefined) => void;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly onPeriodChange: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly invoiceStatus: string | undefined;
  readonly saleStatus: string | undefined;
  readonly documentType: string | undefined;
  readonly series: string;
  readonly onInvoiceStatusChange: (value: string | undefined) => void;
  readonly onSaleStatusChange: (value: string | undefined) => void;
  readonly onDocumentTypeChange: (value: string | undefined) => void;
  readonly onSeriesChange: (value: string) => void;
}

const findOption = (
  options: readonly FilterOption[],
  value: string | undefined,
): FilterOption | null =>
  options.find((option: FilterOption) => option.value === value) ?? null;

const readOptionValue = (newValue: unknown): string | undefined =>
  (newValue as SingleValue<FilterOption>)?.value ?? undefined;

const fieldLabel = "text-xs text-slate-500 mb-1 block";
const fieldWidth = "w-56";

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

export function StoreInvoiceFilters(props: StoreInvoiceFiltersProps) {
  const currentStore: FilterOption | null = findOption(
    props.storeOptions,
    props.storeCode === undefined ? undefined : String(props.storeCode),
  );

  const handleStoreChange = (newValue: unknown): void => {
    const value: string | undefined = readOptionValue(newValue);
    const parsed: number = Number(value);
    props.onStoreChange(
      value !== undefined && !Number.isNaN(parsed) ? parsed : undefined,
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-4 mt-4">
      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_INVOICE_LABELS.storeLabel}
          <RequiredMark />
        </label>
        <SelectInput
          options={props.storeOptions}
          value={currentStore}
          onChange={handleStoreChange}
          isClearable={!props.isStoreLocked}
          isDisabled={props.isStoreLocked}
          placeholder={STORE_INVOICE_LABELS.storePlaceholder}
        />
      </div>

      <div className="flex flex-col">
        <label className={fieldLabel}>
          {STORE_INVOICE_LABELS.periodLabel}
          <RequiredMark />
        </label>
        <PeriodRangeFilter
          start={props.startDate}
          end={props.endDate}
          onChange={props.onPeriodChange}
          label={STORE_INVOICE_LABELS.periodPlaceholder}
          inline
        />
      </div>

      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_INVOICE_LABELS.invoiceStatusLabel}
        </label>
        <SelectInput
          options={INVOICE_STATUS_OPTIONS}
          value={findOption(INVOICE_STATUS_OPTIONS, props.invoiceStatus)}
          onChange={(newValue: unknown) =>
            props.onInvoiceStatusChange(readOptionValue(newValue))
          }
          isClearable
          placeholder={STORE_INVOICE_LABELS.allOption}
        />
      </div>

      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_INVOICE_LABELS.saleStatusLabel}
        </label>
        <SelectInput
          options={SALE_STATUS_OPTIONS}
          value={findOption(SALE_STATUS_OPTIONS, props.saleStatus)}
          onChange={(newValue: unknown) =>
            props.onSaleStatusChange(readOptionValue(newValue))
          }
          isClearable
          placeholder={STORE_INVOICE_LABELS.allOption}
        />
      </div>

      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_INVOICE_LABELS.documentTypeLabel}
        </label>
        <SelectInput
          options={DOCUMENT_TYPE_OPTIONS}
          value={findOption(DOCUMENT_TYPE_OPTIONS, props.documentType)}
          onChange={(newValue: unknown) =>
            props.onDocumentTypeChange(readOptionValue(newValue))
          }
          isClearable
          placeholder={STORE_INVOICE_LABELS.allOption}
        />
      </div>

      <div className="w-32">
        <label className={fieldLabel}>{STORE_INVOICE_LABELS.seriesLabel}</label>
        <Input
          value={props.series}
          onChange={event => props.onSeriesChange(event.target.value)}
          placeholder={STORE_INVOICE_LABELS.seriesPlaceholder}
          maxLength={3}
          className="h-8"
        />
      </div>
    </div>
  );
}
